import { useEffect } from 'react';

import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import { kebabCase, lowerCase } from 'lodash';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';

import CmsContent, { TRenderCmsContent } from '@@/auto/components/CmsContent';
import betterPrivacyPolicyPersonalInformation from '@@/auto/constants/betterPrivacyPolicyPersonalInformation';
import { ICmsField } from '@@/auto/interfaces';
import LayoutSelfService from '@@/common/components/LayoutSelfService';
import { useCmsContent } from '@@/common/hooks/useCmsContent';

import SpinnerView from '../../SpinnerView';
import classes from '../PrivacyPolicyView.module.css';

const cmsModelSlug = 'privacy-policy-v2';

/**
 * TODO:
 *
 * Enhancements:
 * 1. Experiment with liquid js to render nested bullets
 * 2. Make the table data dynamic so this can be used in the future for all partners potentially.
 * 3. Rename file to something less partner specific once we use for other partners.
 */

type TCreateCmsRenderer = (fields: string[]) => TRenderCmsContent;

const createCmsContentRendererForFields: TCreateCmsRenderer = (fieldNames) => ({
  // eslint-disable-next-line react/prop-types
  cmsContent,
}) => {
  const sections: ICmsField[] = [];

  fieldNames.forEach((fieldName) => {
    const field = cmsContent?.fields.find(
      (cmsField) => cmsField.field_template.name === fieldName
    );

    if (field) {
      sections.push(field);
    }
  });

  return (
    <>
      <ul className={classes.condensedList}>
        <li>
          <a href="#better-privacy-policy" className={classes.sectionHeading}>
            <h2>Better Privacy Policy</h2>
          </a>
        </li>
        <li>
          <a href="#toggle-privacy-policy" className={classes.sectionHeading}>
            <h2>Toggle® Privacy Policy</h2>
          </a>
        </li>
      </ul>
      {sections?.map((htmlSection) => {
        const fieldName = kebabCase(htmlSection.field_template.name);
        return (
          <div
            id={fieldName}
            key={htmlSection?.id}
            className={classes[fieldName]}
          >
            {ReactHtmlParser(htmlSection?.text || '', {
              transform: function transformAnchorTags(node, index) {
                if (node.type === 'tag' && node.name === 'h2') {
                  const nodeWithId = {
                    ...node,
                    attribs: {
                      id: kebabCase(node.children[0].data),
                    },
                  };

                  return (
                    <a
                      className={classes.sectionHeading}
                      href={`#${kebabCase(node.children[0].data)}`}
                    >
                      {convertNodeToElement(
                        nodeWithId,
                        index,
                        transformAnchorTags
                      )}
                    </a>
                  );
                }

                return convertNodeToElement(node, index, transformAnchorTags);
              },
            })}
          </div>
        );
      })}
    </>
  );
};

type TBetterPrivacyPolicyView = {
  cmsSlug: string;
};

function BetterPrivacyPolicyView({ cmsSlug }: TBetterPrivacyPolicyView) {
  const cmsContent = useCmsContent(cmsModelSlug);

  useEffect(() => {
    analytics.page();
  }, []);

  if (!cmsContent) {
    return <SpinnerView />;
  }

  return (
    <LayoutSelfService
      className={classes.root}
      header="Privacy Policy"
      backgroundImgSrc={null}
      overrideClasses={{
        children_container: classes.children_container,
      }}
    >
      <CmsContent
        data-testid="privacy-policy-content-1"
        entrySlug={cmsSlug}
        modelSlug={cmsModelSlug}
        renderContent={createCmsContentRendererForFields(['Before Table'])}
      />
      <table className={classes.personalInfoTable}>
        <thead>
          <tr>
            <td>Category</td>
            <td>Examples</td>
          </tr>
        </thead>
        <tbody>
          {betterPrivacyPolicyPersonalInformation.map((item) => {
            const key = lowerCase(item.title);
            return (
              <tr key={key} data-testid={`PersonalInformationTable_${key}`}>
                <td>{item.title}</td>
                <td>{item.examples}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <CmsContent
        data-testid="privacy-policy-content-2"
        entrySlug={cmsSlug}
        modelSlug={cmsModelSlug}
        renderContent={createCmsContentRendererForFields(['After Table'])}
      />
    </LayoutSelfService>
  );
}
export default BetterPrivacyPolicyView;
